/**
 * App Component
 *
 * Root component that sets up providers, routing, and authentication.
 * API key setup is optional - users can skip and configure later in settings.
 */

import { useState, useEffect, useCallback, Component, type ReactNode, type ErrorInfo } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { DataProvider, useData } from '@/context/DataContext';
import { AppProvider, useApp } from '@/context/AppContext';
import { ToastProvider, useToast } from '@/components/ui/toast';
import { SignIn } from '@/pages/SignIn';
import { Privacy } from '@/pages/Privacy';
import { Dashboard } from '@/pages/Dashboard';
import { FeedPage } from '@/pages/FeedPage';
import { SourcesPage } from '@/pages/SourcesPage';
import { TrackingPage } from '@/pages/TrackingPage';
import { SettingsPage } from '@/pages/SettingsPage';
import { AppShell } from '@/components/layout/AppShell';
import { AddEditModal } from '@/components/modals/AddEditModal';
import { ViewModal } from '@/components/modals/ViewModal';
import { DeleteModal } from '@/components/modals/DeleteModal';
import { SettingsModal } from '@/components/modals/SettingsModal';
import { WelcomeModal } from '@/components/onboarding';
import { openRouter } from '@/services/openRouter';
import { Sparkles, AlertTriangle } from 'lucide-react';
import type { Item, TaskItem, DriveWriteErrorDetail } from '@/types';

// ============================================
// APP
// ============================================

export function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <Routes>
          <Route path="/privacy" element={<Privacy />} />
          <Route
            path="*"
            element={
              <ToastProvider>
                <AuthProvider>
                  <AuthGate />
                </AuthProvider>
              </ToastProvider>
            }
          />
        </Routes>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

// ============================================
// ERROR BOUNDARY
// ============================================

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('App error boundary caught:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-[var(--bg-primary)] flex items-center justify-center">
          <div className="text-center max-w-md px-6">
            <div className="w-16 h-16 rounded-2xl bg-red-100 flex items-center justify-center mx-auto mb-6">
              <AlertTriangle className="h-8 w-8 text-red-600" />
            </div>
            <h1 className="text-xl font-display font-bold text-[var(--text-primary)] mb-2">
              Something went wrong
            </h1>
            <p className="text-[var(--text-tertiary)] mb-6">
              An unexpected error occurred. Please reload the page to continue.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors"
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// ============================================
// AUTH GATE
// ============================================

/**
 * Handles authentication state and renders appropriate content.
 * Shows loading screen during initialization, SignIn when not
 * authenticated, and the onboarding gate when authenticated.
 */
function AuthGate() {
  const { user, isInitialized, isLoading } = useAuth();

  // No stored token = no possible session to restore → show landing page instantly
  const hasStoredToken = Boolean(localStorage.getItem('google_access_token'));

  if (!isInitialized || isLoading) {
    if (hasStoredToken) {
      return <LoadingScreen />;
    }
    // New visitors see the landing page immediately (auth initializes in background)
    return (
      <Routes>
        <Route path="/privacy" element={<Privacy />} />
        <Route path="*" element={<SignIn />} />
      </Routes>
    );
  }

  // Not authenticated - show sign in
  if (!user) {
    return (
      <Routes>
        <Route path="*" element={<SignIn />} />
      </Routes>
    );
  }

  // Authenticated - load credentials (non-blocking) and show app
  return <CredentialsLoader />;
}

// ============================================
// CREDENTIALS LOADER
// ============================================

/**
 * Loads API key from Google Drive if available.
 * Does NOT block access - users can use app without API key.
 * AI features will be disabled until key is configured in settings.
 */
function CredentialsLoader() {
  const [isLoading, setIsLoading] = useState(true);

  // Load credentials on mount (non-blocking)
  useEffect(() => {
    const loadCredentials = async () => {
      // First check sessionStorage (already loaded this session)
      if (openRouter.isConfigured()) {
        setIsLoading(false);
        return;
      }

      // Try loading from Google Drive (if user previously configured)
      await openRouter.loadFromDrive();
      setIsLoading(false);
    };

    void loadCredentials();
  }, []);

  // Show loading while checking for saved credentials
  if (isLoading) {
    return <LoadingScreen />;
  }

  // Go straight to app - AI button will be greyed if no key
  return (
    <DataProvider>
      <AppProvider>
        <AuthenticatedRoutes />
      </AppProvider>
    </DataProvider>
  );
}

// ============================================
// GLOBAL MODALS
// ============================================

/**
 * Renders all modals at the top level so they work from any route
 * (Dashboard, FeedPage, etc.). Reads modal state from AppContext
 * and item operations from DataContext.
 */
function GlobalModals() {
  const { modal, modalKey, openModal, closeModal, setActiveTag, activeTag } = useApp();

  const { config, isLoading, updateItem, deleteItem, updateOnboarding } = useData();

  const { error: showError } = useToast();

  // Listen for drive write errors (app-wide, works from any route)
  useEffect(() => {
    const handleDriveError = (event: CustomEvent<DriveWriteErrorDetail>) => {
      const { filename, error: driveError } = event.detail;
      showError('Sync Error', `Failed to save ${filename}: ${driveError.message}`);
    };

    window.addEventListener('drive-write-error', handleDriveError);
    return () => window.removeEventListener('drive-write-error', handleDriveError);
  }, [showError]);

  // Onboarding state
  const onboarding = config.onboarding;
  const showWelcome = !isLoading && !onboarding?.completedWelcome;
  const handleWelcomeComplete = useCallback(() => {
    updateOnboarding({ completedWelcome: true });
  }, [updateOnboarding]);

  const handleEditItem = useCallback(
    (item: Item) => {
      openModal({ mode: 'edit', item });
    },
    [openModal]
  );

  const handleDeleteItem = useCallback(
    (item: Item) => {
      openModal({ mode: 'delete', item });
    },
    [openModal]
  );

  const handleToggleFavorite = useCallback(
    (item: Item) => {
      updateItem({ ...item, isFavorite: !item.isFavorite }, { skipTimestampUpdate: true });
    },
    [updateItem]
  );

  const handleTagClick = useCallback(
    (tag: string) => {
      setActiveTag(activeTag === tag ? null : tag);
    },
    [activeTag, setActiveTag]
  );

  const handleToggleComplete = useCallback(
    (item: TaskItem, completed: boolean) => {
      updateItem({ ...item, isCompleted: completed }, { skipTimestampUpdate: true });
    },
    [updateItem]
  );

  const handleMarkProcessed = useCallback(
    (item: Item) => {
      const newTags = item.tags.filter((t) => t !== 'inbox');
      updateItem({ ...item, tags: newTags }, { skipTimestampUpdate: true });
      closeModal();
    },
    [updateItem, closeModal]
  );

  const handleMoveToInbox = useCallback(
    (item: Item) => {
      if (!item.tags.includes('inbox')) {
        updateItem({ ...item, tags: [...item.tags, 'inbox'] }, { skipTimestampUpdate: true });
      }
      closeModal();
    },
    [updateItem, closeModal]
  );

  const handleDeleteConfirm = useCallback(
    async (item: Item) => {
      await deleteItem(item);
      closeModal();
    },
    [deleteItem, closeModal]
  );

  return (
    <>
      <AddEditModal
        key={modal.mode === 'edit' ? modal.item?.id : `add-${modalKey}`}
        isOpen={modal.isOpen && (modal.mode === 'add' || modal.mode === 'edit')}
        mode={modal.mode === 'edit' ? 'edit' : 'add'}
        item={modal.item}
        itemType={modal.itemType}
        prefillTags={modal.prefillTags}
        prefillUrl={modal.prefillUrl}
        prefillTitle={modal.prefillTitle}
        prefillContent={modal.prefillContent}
        autoStartVoice={modal.autoStartVoice}
        onClose={closeModal}
      />

      <ViewModal
        isOpen={modal.isOpen && modal.mode === 'view'}
        item={modal.item}
        onClose={closeModal}
        onEdit={handleEditItem}
        onDelete={handleDeleteItem}
        onMarkProcessed={handleMarkProcessed}
        onMoveToInbox={handleMoveToInbox}
        onToggleFavorite={handleToggleFavorite}
        onToggleComplete={handleToggleComplete}
        onTagClick={handleTagClick}
      />

      <DeleteModal
        isOpen={modal.isOpen && modal.mode === 'delete'}
        item={modal.item}
        onClose={closeModal}
        onConfirm={handleDeleteConfirm}
      />

      <SettingsModal isOpen={modal.isOpen && modal.mode === 'settings'} onClose={closeModal} />

      {/* Welcome Modal (first sign-in only) */}
      <WelcomeModal isOpen={showWelcome} onComplete={handleWelcomeComplete} />
    </>
  );
}

// ============================================
// HOME RESOLVER
// ============================================

function HomeResolver() {
  const { config, allItems, isLoading } = useData();
  if (isLoading) return <LoadingScreen />;
  // New users: no onboarding completed AND no saved items → feed
  // Returning users with old config (no onboarding key) but items → dashboard
  const isNewUser = !config.onboarding?.completedWelcome && allItems.length === 0;
  if (isNewUser) return <Navigate to="/feed" replace />;
  return <Dashboard />;
}

// ============================================
// AUTHENTICATED ROUTES
// ============================================

function AuthenticatedRoutes() {
  return (
    <AppShell>
      <Routes>
        <Route path="/" element={<HomeResolver />} />
        <Route path="/feed" element={<FeedPage />} />
        <Route path="/sources" element={<SourcesPage />} />
        <Route path="/tracking" element={<TrackingPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <GlobalModals />
    </AppShell>
  );
}

// ============================================
// LOADING SCREEN
// ============================================

function LoadingScreen() {
  return (
    <div className="min-h-screen bg-[var(--bg-primary)] flex items-center justify-center">
      <div className="text-center animate-fade-in-up">
        {/* Logo with pulsing effect */}
        <div className="w-20 h-20 rounded-2xl bg-brand-600 flex items-center justify-center mx-auto mb-6 shadow-lg animate-brand-pulse">
          <Sparkles className="h-10 w-10 text-white" />
        </div>

        {/* Title */}
        <h1 className="text-2xl font-display font-bold text-[var(--text-primary)] mb-2">
          Personal Insights
        </h1>
        <p className="text-[var(--text-tertiary)] mb-8">Loading your workspace...</p>

        {/* Loading dots */}
        <div className="flex justify-center gap-1.5">
          <div className="w-2 h-2 rounded-full bg-brand-600 animate-bounce-soft" />
          <div className="w-2 h-2 rounded-full bg-brand-600 animate-bounce-soft animation-delay-100" />
          <div className="w-2 h-2 rounded-full bg-brand-600 animate-bounce-soft animation-delay-200" />
        </div>
      </div>
    </div>
  );
}

// Default export for compatibility
export default App;
